import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "./brand";
import Button from "./Button";
import { IconChevronRight, IconVerified } from "./brand/icons";

const APP_LOGIN_URL = "https://app.socialspectrum.org/";

const menuitems = [
  { name: "Home", url: "/" },
  { name: "Our Story", url: "/ourstory" },
  { name: "Our App", url: "/ourapp" },
  { name: "Events", url: "/events" },
  { name: "Become an Ally", url: "/becomeanally" },
  { name: "Newsletter", url: "/resources" },
  { name: "Blog", url: "/blog" },
];

const Header = () => {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const menuButtonRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // A new page always starts with the drawer closed.
  useEffect(() => setMenuOpen(false), [pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  const handleDownload = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/android/i.test(userAgent) || /Windows/i.test(userAgent)) {
      window.plausible("Play Store Click");
      window.location.href =
        "https://play.google.com/store/apps/details?id=com.social_spectrum.mobile";
    } else if (/iPad|iPhone|iPod|ios/.test(userAgent) && !window.MSStream) {
      window.plausible("App Store Click");
      window.location.href =
        "https://apps.apple.com/us/app/social-spectrum/id6473112455";
    } else {
      window.plausible("Play Store Click");
      window.open(
        "https://play.google.com/store/apps/details?id=com.social_spectrum.mobile",
        "_blank"
      );
    }
  };

  const isActive = (url) =>
    url === "/"
      ? pathname === "/"
      : pathname === url || pathname.startsWith(`${url}/`);

  return (
    <header
      className={`sticky top-0 z-50 w-full bg-surface-white transition-shadow duration-200 ${
        scrolled ? "shadow-brand-md" : "border-b border-cloud-mist"
      }`}
    >
      <div className="flex justify-between items-center w-full px-5 sm:px-6 md:px-10 max-w-7xl mx-auto h-20">
        <Link
          to="/"
          className="flex items-center shrink-0 rounded-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue"
          aria-label="Social Spectrum Home"
        >
          <span className="md:hidden">
            <Logo variant="primary" height={30} compact />
          </span>
          <span className="hidden md:inline-flex">
            <Logo variant="primary" height={40} compact />
          </span>
        </Link>

        <nav
          className="hidden xl:flex items-center gap-7 text-[14px] font-headline"
          aria-label="Primary"
        >
          {menuitems.map((item) => (
            <Link
              key={item.url}
              to={item.url}
              aria-current={isActive(item.url) ? "page" : undefined}
              className={`relative inline-block leading-none transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue ${
                isActive(item.url)
                  ? "text-spectrum-blue font-bold after:absolute after:inset-x-0 after:-bottom-1.5 after:h-0.5 after:rounded-full after:bg-spectrum-blue after:content-['']"
                  : "text-ink opacity-80 font-medium hover:text-spectrum-blue hover:opacity-100"
              }`}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <Button
            href={APP_LOGIN_URL}
            variant="link"
            className="hidden sm:inline-flex !text-ink hover:!text-spectrum-blue"
          >
            Login / Sign up
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleDownload}
            className="hidden sm:inline-flex"
          >
            Download Now
          </Button>

          <button
            ref={menuButtonRef}
            type="button"
            className="xl:hidden flex items-center justify-center w-11 h-11 -mr-2 rounded-brand border border-cloud-mist bg-surface-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-blue"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-primary-nav"
          >
            <span className="relative block w-5 h-[14px]" aria-hidden="true">
              <span
                className={`absolute left-0 block w-5 h-[2px] bg-spectrum-blue rounded-brand transition-all duration-200 ${
                  menuOpen ? "top-[6px] rotate-45" : "top-0"
                }`}
              />
              <span
                className={`absolute top-[6px] left-0 block w-5 h-[2px] bg-spectrum-blue rounded-brand transition-opacity duration-150 ${
                  menuOpen ? "opacity-0" : "opacity-100"
                }`}
              />
              <span
                className={`absolute left-0 block w-5 h-[2px] bg-spectrum-blue rounded-brand transition-all duration-200 ${
                  menuOpen ? "top-[6px] -rotate-45" : "top-[12px]"
                }`}
              />
            </span>
          </button>
        </div>
      </div>

      {/* Drawer + backdrop — the bar itself stays above both */}
      <div
        className={`xl:hidden fixed inset-0 top-20 bg-black/40 transition-opacity duration-200 ${
          menuOpen
            ? "opacity-100"
            : "pointer-events-none invisible opacity-0"
        }`}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <div
        id="mobile-primary-nav"
        className={`xl:hidden absolute left-0 right-0 top-full origin-top rounded-b-2xl border-t border-cloud-mist bg-surface-white shadow-brand-lg transition-[opacity,transform] duration-200 ${
          menuOpen
            ? "visible translate-y-0 opacity-100"
            : "invisible -translate-y-2 opacity-0"
        }`}
        aria-label="Mobile primary"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 py-4">
          <nav className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-x-3">
            {menuitems.map((item) => {
              const active = isActive(item.url);
              return (
                <Link
                  key={item.url}
                  to={item.url}
                  aria-current={active ? "page" : undefined}
                  className={`flex items-center justify-between gap-3 rounded-brand px-4 py-3 text-left font-headline text-[15px] transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-sky-blue ${
                    active
                      ? "bg-cloud-mist text-spectrum-blue font-bold"
                      : "text-ink font-medium hover:bg-mist-soft"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {active && <IconVerified size={16} />}
                    {item.name}
                  </span>
                  <IconChevronRight
                    size={16}
                    className={active ? "text-spectrum-blue" : "text-cloud-mist"}
                  />
                </Link>
              );
            })}
          </nav>

          <div className="mt-4 pt-4 border-t border-cloud-mist flex flex-col sm:flex-row gap-3">
            <Button
              href={APP_LOGIN_URL}
              variant="secondary"
              size="sm"
              className="flex-1"
            >
              Login / Sign up
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleDownload}
              className="flex-1"
            >
              Download Now
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
